import { SafeAreaView, ScrollView, TouchableOpacity, View } from 'react-native';
import React, { useCallback, useMemo, useState } from 'react';
import MarisaServiceLogoSVG from '~/assets/svgs/marisaServicesLogo.svg';
import MarisaSVG from '~/assets/svgs/marisa.svg';
import { Button, Input, Text } from '~/components';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isCpf, isEmail } from '~/utils/yup';
import ErrorSVG from '~/assets/svgs/error.svg';
import CloseErrorSVG from '~/assets/svgs/closeError.svg';
import CloseSVG from '~/assets/svgs/close.svg';
import SignInUseCase from '~/useCases/HybrisAuthenticateUseCases/SignInUseCase';
import useAppNavigation from '~/hooks/useAppNavigation';

const schema = yup
  .object()
  .shape({
    username: yup
      .string()
      .when({
        is: (value: string) => /^\d+$/.test((value || '').substring(0, 3)),
        then: isCpf,
        otherwise: isEmail,
      })
      .required('O Email ou CPF são obrigatórios.'),
    password: yup
      .string()
      .min(6, 'A senha deve ter pelo menos 6 dígitos.')
      .required('A senha é obrigatória.'),
  })
  .required();

interface SignInForm {
  username: string;
  password: string;
}

const SignIn = () => {
  const [error, setError] = useState<string>('');
  const { navigate, canGoBack, goBack } = useAppNavigation();

  const {
    control,
    formState: { errors, isSubmitting },
    watch,
    handleSubmit,
  } = useForm<SignInForm>({
    resolver: yupResolver(schema),
  });

  const username = watch('username');

  const isCPF = useMemo(() => {
    if (username) {
      return /^\d+$/.test(username?.substring(0, 3));
    }

    return false;
  }, [username]);

  const onSubmit = useCallback(
    async (data: SignInForm) => {
      try {
        await SignInUseCase(data.username, data.password);

        if (canGoBack()) {
          goBack();

          return;
        }

        navigate('MarisaServicos_MarisaTakesCareDetails');
      } catch (e) {
        if (e instanceof Error) {
          setError(e.message);
        }
      }
    },
    [canGoBack, goBack, navigate]
  );

  return (
    <SafeAreaView className="bg-white h-full">
      <ScrollView
        contentContainerStyle={{
          flexGrow: 1,
          flexDirection: 'row',
        }}
        className="h-full"
      >
        <View className="flex-1 bg-primary-600 items-center justify-center hidden lg:flex">
          <MarisaServiceLogoSVG />
        </View>

        <View className="flex-1 bg-white items-center justify-start px-10 pt-24 lg:justify-center lg:pt-16">
          <MarisaSVG />

          <View>
            <Text className="text-[22px] mt-8 mb-14 lg:w-[450px]">
              Olá, digite seu{' '}
              <Text className="font-medium text-primary-600">
                e-mail ou CPF
              </Text>
            </Text>

            <Input
              name="username"
              control={control}
              label="Email ou CPF"
              placeholder="Insira o seu email ou CPF"
              error={errors.username?.message}
              maskConfig={isCPF ? { type: 'cpf' } : undefined}
            />

            <Input
              name="password"
              control={control}
              label="Senha"
              placeholder="Insira sua senha"
              error={errors.password?.message}
              isPassword
            />

            <TouchableOpacity
              activeOpacity={0.6}
              onPress={() => navigate('MarisaServicos_ForgotPassword')}
            >
              <Text className="text-right text-primary-600 uppercase font-medium text-xs mb-4">
                Esqueceu a senha?
              </Text>
            </TouchableOpacity>

            <View className="border-t border-t-gray-100 mt-2">
              <Button
                disabled={isSubmitting}
                onPress={handleSubmit(onSubmit)}
                className="mt-4"
              >
                Entrar
              </Button>

              <Text className="text-center uppercase mt-9 text-gray-400 text-sm">
                Você é nova por aqui?{' '}
                <TouchableOpacity
                  onPress={() => navigate('MarisaServicos_CreateAccount', {})}
                >
                  <Text className="font-medium text-primary-600">
                    Crie sua conta
                  </Text>
                </TouchableOpacity>
              </Text>

              <Text className="text-feedback-neutral-textMain text-xs text-center mt-10">
                *O acesso é o mesmo utilizado no Site e App da Marisa
              </Text>

              {error ? (
                <View className="flex-row border rounded-lg border-feedback-error-main bg-feedback-error-bkg p-3 mt-8 items-center justify-between">
                  <View className="flex-row items-center">
                    <ErrorSVG />

                    <Text className="ml-2 text-feedback-error-text text-xs">
                      {error}
                    </Text>
                  </View>

                  <TouchableOpacity onPress={() => setError('')}>
                    <CloseErrorSVG />
                  </TouchableOpacity>
                </View>
              ) : (
                <View className="mt-8 h-[42px]" />
              )}
            </View>
          </View>
        </View>
        <TouchableOpacity
          className="absolute top-5 right-5 z-10"
          onPress={goBack}
        >
          <CloseSVG height={24} width={24} />
        </TouchableOpacity>
      </ScrollView>
    </SafeAreaView>
  );
};

export default SignIn;
